import { create } from "zustand";
import logger from "../lib/logger";
import type { PostItemInput, WallPost } from "../api/content";
import { canRetry, isActive, uploadQueue, useUploadQueueStore, type UploadItem, type VideoCompletion } from "../store/uploadQueue";
import { toPostItems, withVideoSend, type ComposerItem, type VideoSendState } from "../components/wall/composerModel";

/**
 * Wall post videos (spec 2026-10-23, web). Each new video in the post editor is an upload-queue item
 * (target `{kind:"wall", draftId, itemKey}`); its READY hand-off lands here, outside the editor.
 *
 * Closing the editor while its videos still send queues the post: it lives in this store (so it
 * survives in-app navigation, not a tab reload) and is created once every video is READY. A video of
 * its own that fails or is cancelled drops the post and shows "Post not published". A post is
 * created at most once per draft id, whether from the open editor or from the queue.
 */

export interface QueuedPost {
  draftId: string;
  /** The editor's items when it closed; new videos carry their queue ids. */
  items: ComposerItem[];
  /** Sends the post with these items (create or update), and refreshes the feeds. */
  save(items: PostItemInput[]): Promise<WallPost>;
  status: "waiting" | "saving";
}

export interface WallPostNotice {
  draftId: string;
  /** The video whose failure dropped the post (none when it was cancelled or the save failed). */
  failedName?: string;
}

interface WallVideoPostState {
  /** READY videos by queue id (fileId + poster), until their draft is posted or discarded. */
  ready: Record<string, VideoCompletion>;
  queued: Record<string, QueuedPost>;
  /** "Post not published" for the toast, until dismissed. */
  notice: WallPostNotice | null;
}

export const useWallVideoPostStore = create<WallVideoPostState>(() => ({ ready: {}, queued: {}, notice: null }));

/** An upload-queue item as the editor's video row reads it. */
export function videoSendOf(item: UploadItem | undefined): VideoSendState | undefined {
  if (!item) return undefined;
  return {
    phase: item.phase,
    progress: item.progress,
    bytesSent: item.bytesSent,
    bytesTotal: item.bytesTotal,
    posterUrl: item.posterUrl,
    durationMs: item.durationMs,
    // UNSUPPORTED_TYPE and REJECTED are chat-album codes only (wall videos are type-checked when picked).
    errorCode: item.errorCode === "TOO_LARGE" ? "TOO_LARGE" : item.errorCode == null ? undefined : "FAILED",
    errorDetail: item.errorDetail,
    retryable: canRetry(item),
  };
}

/** Drafts already posted (or posting): the exactly-once guard. */
const claimed = new Set<string>();
/** Queue items to drop once they stop (a cancel aborts asynchronously). */
const dropWhenStopped = new Set<string>();

const uploadIdsOf = (items: ComposerItem[]): string[] =>
  items.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : []));

function forgetUploads(ids: string[]): void {
  useWallVideoPostStore.setState((s) => {
    if (!ids.some((id) => s.ready[id])) return s;
    const ready = { ...s.ready };
    for (const id of ids) delete ready[id];
    return { ready };
  });
  for (const id of ids) {
    const item = uploadQueue.get(id);
    if (item && isActive(item)) {
      dropWhenStopped.add(id);
      uploadQueue.cancel(id);
    } else {
      uploadQueue.remove(id);
    }
  }
}

function dropQueued(draftId: string): void {
  useWallVideoPostStore.setState((s) => {
    if (!s.queued[draftId]) return s;
    const queued = { ...s.queued };
    delete queued[draftId];
    return { queued };
  });
}

/** Marks the draft posted; false when it already was (a second create must not happen). */
export function claimDraft(draftId: string): boolean {
  if (claimed.has(draftId)) return false;
  claimed.add(draftId);
  return true;
}

/** A direct save from the editor failed: the draft may be posted again. */
export function releaseDraft(draftId: string): void {
  claimed.delete(draftId);
}

/** The items to send once every video is READY (each video carries its fileId and poster). */
function resolvedItems(items: ComposerItem[]): ComposerItem[] {
  const { ready } = useWallVideoPostStore.getState();
  const { items: queue } = useUploadQueueStore.getState();
  return items.map((it) => (it.kind === "file" && it.uploadId != null ? withVideoSend(it, videoSendOf(queue[it.uploadId]), ready[it.uploadId]) : it));
}

async function saveQueued(post: QueuedPost): Promise<void> {
  if (!claimDraft(post.draftId)) {
    dropQueued(post.draftId);
    return;
  }
  useWallVideoPostStore.setState((s) => ({ queued: { ...s.queued, [post.draftId]: { ...post, status: "saving" } } }));
  const ids = uploadIdsOf(post.items);
  try {
    await post.save(toPostItems(resolvedItems(post.items)));
  } catch (err) {
    logger.error("[wallVideoPost] queued post failed", err);
    useWallVideoPostStore.setState({ notice: { draftId: post.draftId } });
  }
  dropQueued(post.draftId);
  forgetUploads(ids);
}

/** Moves every waiting queued post on: create it once all its videos are READY, drop it if one is lost. */
function advance(): void {
  const { queued } = useWallVideoPostStore.getState();
  for (const draftId of Object.keys(queued)) {
    // Read it again: a nested advance (a queue change while dropping another post) may have moved it on.
    const post = useWallVideoPostStore.getState().queued[draftId];
    if (!post || post.status !== "waiting") continue;
    const { ready } = useWallVideoPostStore.getState();
    const { items } = useUploadQueueStore.getState();
    const ids = uploadIdsOf(post.items);
    const lost = ids.find((id) => !ready[id] && (!items[id] || items[id].phase === "error" || items[id].phase === "cancelled"));
    if (lost) {
      const failed = items[lost]?.phase === "error" ? items[lost] : undefined;
      dropQueued(post.draftId);
      useWallVideoPostStore.setState({ notice: { draftId: post.draftId, failedName: failed?.fileName } });
      forgetUploads(ids);
      continue;
    }
    if (ids.every((id) => ready[id])) void saveQueued(post);
  }
}

function onVideoReady(c: VideoCompletion): void {
  useWallVideoPostStore.setState((s) => ({ ready: { ...s.ready, [c.id]: c } }));
  advance();
}

function onQueueChange(): void {
  const { items } = useUploadQueueStore.getState();
  for (const id of [...dropWhenStopped]) {
    const item: UploadItem | undefined = items[id];
    if (!item) dropWhenStopped.delete(id);
    else if (!isActive(item)) {
      dropWhenStopped.delete(id);
      uploadQueue.remove(id);
    }
  }
  advance();
}

let wired: (() => void) | null = null;

function ensureWired(): void {
  if (wired) return;
  const offComplete = uploadQueue.onComplete("wall", onVideoReady);
  const offQueue = useUploadQueueStore.subscribe(onQueueChange);
  wired = () => {
    offComplete();
    offQueue();
  };
}

/** Sends one new editor video through the app-wide queue; returns its queue id. */
export function enqueueWallVideo(file: File, draftId: string, itemKey: number): string {
  ensureWired();
  return uploadQueue.enqueueVideo({ file, target: { kind: "wall", draftId, itemKey: String(itemKey) } });
}

/** The editor closed with videos still sending: the post goes out once they are READY. */
export function queueWallPost(post: Omit<QueuedPost, "status">): void {
  ensureWired();
  useWallVideoPostStore.setState((s) => ({ queued: { ...s.queued, [post.draftId]: { ...post, status: "waiting" } } }));
  advance();
}

export function isDraftQueued(draftId: string): boolean {
  return !!useWallVideoPostStore.getState().queued[draftId];
}

/**
 * The editor is done with these videos: posted (their queue items and posters are freed), removed
 * from the list, or the draft discarded (the ones still sending are cancelled).
 */
export function releaseWallVideos(ids: string[]): void {
  forgetUploads(ids);
}

export function dismissWallPostNotice(): void {
  useWallVideoPostStore.setState({ notice: null });
}

export function resetWallVideoPostForTests(): void {
  wired?.();
  wired = null;
  claimed.clear();
  dropWhenStopped.clear();
  useWallVideoPostStore.setState({ ready: {}, queued: {}, notice: null });
}
